import type React from "react";
import {type ReactNode, useCallback, useRef} from "react";

import {ShapeType, type TLevelObject, config} from "@repo/shared";

import TextureBlackHoleInstanceDefinition from "~/three/attractor/TextureBlackHoleInstanceDefinition";
import CircleInstanceDefinition from "~/three/circle/CircleInstanceDefinition";
import {InstanceManager} from "~/three/instanced-rendering/InstanceManager";
import LineInstanceDefinition from "~/three/line/LineInstanceDefinition";

import {GameStateContext} from "./GameStateContext";

export interface InstanceTypeData {
  manager: InstanceManager;
  material: React.ReactNode;
  geometry: React.ReactNode;
}

export const GameStateProvider: React.FC<{children: ReactNode}> = ({
  children,
}) => {
  const instanceTypes = useRef<[ShapeType, InstanceTypeData][]>([
    [
      ShapeType.ATTRACTOR,
      {
        manager: new InstanceManager(config),
        // material: <AttractorInstanceDefinition />,
        // material: <BlackHoleInstanceDefinition />,
        material: <TextureBlackHoleInstanceDefinition />,
        geometry: <planeGeometry args={[1, 1]} />,
      },
    ],
    [
      ShapeType.CIRCLE,
      {
        manager: new InstanceManager(config),
        material: <CircleInstanceDefinition />,
        geometry: <circleGeometry args={[0.5, 16]} />,
      },
    ],
    [
      ShapeType.RECTANGLE,
      {
        manager: new InstanceManager(config),
        material: <LineInstanceDefinition />,
        geometry: <planeGeometry args={[1, 1]} />,
      },
    ],
  ]);

  const getInstanceType = useCallback((type: ShapeType): InstanceTypeData => {
    const instanceType = instanceTypes.current.find(([t]) => t === type);
    if (!instanceType) {
      throw new Error(`ShapeType ${type} not found in instanceTypes.`);
    }
    return instanceType[1];
  }, []);

  const getInstanceTypes = useCallback(
    (filterFn?: (type: ShapeType) => boolean): InstanceTypeData[] =>
      instanceTypes.current
        .filter(([type]) => (filterFn ? filterFn(type) : true))
        .map(([_type, data]) => data),
    [],
  );

  const getFullInstanceTypes = useCallback(
    (
      filterFn?: (type: ShapeType) => boolean,
    ): [ShapeType, InstanceTypeData][] =>
      instanceTypes.current
        .filter(([type]) => (filterFn ? filterFn(type) : true))
        .map(([type, data]) => [type, data]),
    [],
  );

  const add = useCallback(
    (objects: TLevelObject[]) => {
      return objects.map((o) =>
        getInstanceType(config.objectTypes[o.type].shape).manager.addInstance(
          o,
        ),
      );
    },
    [getInstanceType],
  );

  const getAllInstances = useCallback(() => {
    return instanceTypes.current.flatMap(([_, data]) =>
      data.manager.getAllInstances(),
    );
  }, []);

  const getSelectedInstances = useCallback(() => {
    return instanceTypes.current.flatMap(([_, data]) =>
      data.manager.getSelectedInstances(),
    );
  }, []);

  const clear = useCallback(() => {
    for (const type of getInstanceTypes()) {
      type.manager.clear();
    }
  }, [getInstanceTypes]);

  return (
    <GameStateContext.Provider
      value={{
        add,
        getAllInstances,
        getSelectedInstances,
        getInstanceType,
        getInstanceTypes,
        getFullInstanceTypes,
        clear,
      }}
    >
      {children}
    </GameStateContext.Provider>
  );
};
